// Las pantallas de ALTA de la biblioteca de adjuntos: subir un producto, crear o subir
// un modelo, subir una pose, subir un fondo. Son las MISMAS pantallas que usan los
// selectores de los modos —`SubirProductoInline`, `CreateView` y `UploadFlow` del
// picker de modelos, `ExplicadorSubirPose` del de poses, `FondoUploadView` del de
// fondos—, montadas acá con un "Volver" arriba; no hay una segunda versión de ninguna
// (ADR-2026-09-12-la-biblioteca-agrega-en-todas-las-pestanas).
//
// Lo que sale de cada alta vuelve al panel por callbacks: refrescar el catálogo que
// cambió y, cuando la pantalla de origen también lo hace (modelo y fondo), dejar lo
// creado adjunto en el chat. La subida de la pose corre en el panel —que mientras tanto
// muestra el spinner en su tarjeta, como el sheet—: acá sólo se elige la foto.

import { useRef, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { CreateView, UploadFlow } from '../../pages/v2/ModelPicker';
import { SubirProductoInline } from '../../pages/v2/components/SubirProductoDrawer';
import { ExplicadorSubirPose } from '../../pages/v2/PosePicker';
import { FondoUploadView } from '../../pages/v2/FondoPicker';
import type { ModelV2 } from '../../pages/v2/modelsApi';
import type { FondoGuardado } from '../../pages/v2/fondosApi';
import { ALTAS_POR_PESTANA, type AltaDeBiblioteca as Alta } from './bibliotecaDeAdjuntos';
import { CuerpoDeAlta } from '../../pages/v2/components/altaEnDosColumnas';

/** Las altas con pantalla propia. `subir-imagen` no tiene: es un file picker directo. */
export type AltaConPantalla = Exclude<Alta, 'subir-imagen'>;

interface Props {
  alta: AltaConPantalla;
  usuarioId: string | null;
  companyId: string | null;
  brandId: string | null;
  plan: string;
  rubro: string | null;
  /** La pose elegida se está procesando (el panel lo sabe; acá se apaga el CTA). */
  subiendoPose: boolean;
  onVolver: () => void;
  onProductosRefrescados: () => void;
  onModelosRefrescados: () => void;
  /** El modelo quedó guardado: el panel lo adjunta (como hace el selector al "Utilizar"). */
  onModeloListo: (modelo: ModelV2) => void;
  onFondosRefrescados: () => void;
  /** El fondo quedó guardado: el panel lo adjunta (como hace el selector al confirmar). */
  onFondoListo: (fondo: FondoGuardado) => void;
  /** Se eligió la foto de la pose: el panel la sube y refresca "Mis poses". */
  onFotoDePose: (archivo: File) => void;
  /**
   * El host ya pone el "Volver" en su cabecera y acá no se dibuja el propio
   * (ADR-2026-09-22-las-altas-de-todas-las-fuentes-en-el-selector). Lo pide el selector
   * de fuentes de la caja del Agente, que hospeda estas mismas pantallas en una ventana
   * con flecha arriba a la izquierda. El panel de la biblioteca no lo pasa: ahí el
   * "Volver" de acá es la única salida.
   */
  sinVolver?: boolean;
  /** Sólo para el alta de producto: el alta en bloque delegada al host (ver
   *  `SubirProductoInline`). */
  onSubirVarios?: () => void;
  /**
   * El host es una ventana ANCHA (el selector de fuentes de la caja del Agente, 980) y
   * no la columna de un sheet: las pantallas se dibujan en DOS columnas —lo que se hace
   * a la izquierda, el ejemplo y los tips a la derecha— con una cabecera propia y una
   * barra de CTA de borde a borde
   * (ADR-2026-09-24-las-altas-del-selector-de-fuentes-en-dos-columnas). El panel lateral
   * de la biblioteca no lo pasa: ahí siguen siendo una columna apilada.
   */
  ancho?: boolean;
}

const entradaDeAlta = (alta: Alta) =>
  Object.values(ALTAS_POR_PESTANA)
    .flat()
    .find((a) => a.id === alta);

const tituloDeAlta = (alta: Alta): string => entradaDeAlta(alta)?.titulo ?? '';

export const AltaDeBiblioteca: React.FC<Props> = ({
  alta,
  usuarioId,
  companyId,
  brandId,
  plan,
  rubro,
  subiendoPose,
  onVolver,
  onProductosRefrescados,
  onModelosRefrescados,
  onModeloListo,
  onFondosRefrescados,
  onFondoListo,
  onFotoDePose,
  sinVolver = false,
  onSubirVarios,
  ancho = false,
}) => {
  const inputPoseRef = useRef<HTMLInputElement | null>(null);

  // El "Volver" de arriba. La pantalla de la pose ya trae el suyo con su título (en
  // desktop), y la de producto trae su encabezado con el título: ahí va sólo el botón.
  // "Crear modelo" tiene pasos después del formulario (elegir candidato, preview) que
  // traen su propio título: ahí la cabecera del alta se saca.
  const [enFormulario, setEnFormulario] = useState(true);
  const conTitulo = alta === 'crear-modelo' || alta === 'subir-modelo' || alta === 'subir-fondo';
  // Con el "Volver" del host queda igual el TÍTULO —estas tres pantallas no traen uno y
  // sin él la ventana no dice qué se está haciendo—, pero sin el botón.
  const soloTitulo = sinVolver && conTitulo && (
    <p className="shrink-0 px-4 pt-3 text-[15px] font-semibold text-brand-black">
      {tituloDeAlta(alta)}
    </p>
  );
  const volver = !sinVolver && alta !== 'subir-pose' && (
    <div className="flex shrink-0 items-center gap-2 px-3 pt-3">
      <button
        type="button"
        onClick={onVolver}
        className="inline-flex items-center gap-1 rounded-full py-1.5 pl-2 pr-3 text-[13px] font-semibold text-brand-primary transition-colors hover:bg-[#E9EDFD]"
      >
        <ChevronLeft className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
        Volver
      </button>
      {conTitulo && (
        <p className="min-w-0 flex-1 truncate text-[14px] font-semibold text-brand-black">
          {tituloDeAlta(alta)}
        </p>
      )}
    </div>
  );

  // ── El host es ANCHO: cabecera propia + la pantalla en dos columnas ──
  // ADR-2026-09-24-las-altas-del-selector-de-fuentes-en-dos-columnas. El "Volver" lo
  // sigue poniendo la cabecera del pop-up (dice a qué fuente vuelve); acá va el título
  // de lo que se está haciendo, que es lo que esa cabecera no dice.
  if (ancho) {
    return (
      <div className="h-full animate-mode-swap">
        <CuerpoDeAlta
          titulo={tituloDeAlta(alta)}
          bajada={entradaDeAlta(alta)?.bajada}
          sinCabecera={alta === 'crear-modelo' && !enFormulario}
        >
          {alta === 'subir-producto' && (
            <SubirProductoInline
              ancho
              userCompanyId={companyId}
              brandId={brandId}
              rubro={rubro}
              userPlan={plan}
              onProductsRefreshed={onProductosRefrescados}
              onSalir={onVolver}
              onSubirVarios={onSubirVarios}
            />
          )}
          {alta === 'crear-modelo' && (
            <CreateView
              ancho
              onEnFormulario={setEnFormulario}
              userId={usuarioId}
              companyId={companyId}
              onModelCreated={onModelosRefrescados}
              onBack={onVolver}
              onConfirmModel={(modelo) => {
                onModeloListo(modelo);
                onVolver();
              }}
            />
          )}
          {alta === 'subir-modelo' && (
            <UploadFlow
              ancho
              userId={usuarioId}
              companyId={companyId}
              onModelCreated={onModelosRefrescados}
              onUtilizar={(modelo) => {
                onModeloListo(modelo);
                onVolver();
              }}
            />
          )}
          {alta === 'subir-pose' && (
            <ExplicadorSubirPose
              ancho
              uploading={subiendoPose}
              onVolver={onVolver}
              onElegirFoto={() => inputPoseRef.current?.click()}
              sinVolver={sinVolver}
              onFoto={(archivo) => {
                onFotoDePose(archivo);
                // Como con el input: elegida la foto se vuelve a la grilla, donde la
                // tarjeta muestra el spinner y después aparece la pose.
                onVolver();
              }}
            />
          )}
          {alta === 'subir-fondo' && (
            <FondoUploadView
              ancho
              companyId={companyId}
              onConfirm={(guardado) => {
                onFondoListo(guardado);
                onVolver();
              }}
              onFondoSaved={onFondosRefrescados}
            />
          )}
        </CuerpoDeAlta>
      </div>
    );
  }

  if (alta === 'subir-producto') {
    return (
      <>
        {volver}
        {soloTitulo}
        {/* `SubirProductoInline` arma header / scroll / footer con `h-full`: necesita
            una columna que le dé el alto, no un scroller. */}
        <div className="flex min-h-0 flex-1 flex-col animate-mode-swap">
          <SubirProductoInline
            userCompanyId={companyId}
            brandId={brandId}
            rubro={rubro}
            userPlan={plan}
            onProductsRefreshed={onProductosRefrescados}
            onSalir={onVolver}
            onSubirVarios={onSubirVarios}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {volver}
      {soloTitulo}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6 pt-2 animate-mode-swap" style={{ scrollbarWidth: 'thin' }}>
        {alta === 'crear-modelo' && (
          <CreateView
            userId={usuarioId}
            companyId={companyId}
            onModelCreated={onModelosRefrescados}
            onBack={onVolver}
            onConfirmModel={(modelo) => {
              onModeloListo(modelo);
              onVolver();
            }}
          />
        )}
        {alta === 'subir-modelo' && (
          <UploadFlow
            userId={usuarioId}
            companyId={companyId}
            onModelCreated={onModelosRefrescados}
            onUtilizar={(modelo) => {
              onModeloListo(modelo);
              onVolver();
            }}
          />
        )}
        {alta === 'subir-pose' && (
          <>
            <ExplicadorSubirPose
              uploading={subiendoPose}
              onVolver={onVolver}
              onElegirFoto={() => inputPoseRef.current?.click()}
              sinVolver={sinVolver}
            />
            <input
              ref={inputPoseRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.currentTarget.value = '';
                if (!f) return;
                onFotoDePose(f);
                // Como en el sheet: elegida la foto se vuelve a la grilla, donde la
                // tarjeta muestra el spinner y después aparece la pose.
                onVolver();
              }}
            />
          </>
        )}
        {alta === 'subir-fondo' && (
          <FondoUploadView
            companyId={companyId}
            onConfirm={(guardado) => {
              onFondoListo(guardado);
              onVolver();
            }}
            onFondoSaved={onFondosRefrescados}
          />
        )}
      </div>
    </>
  );
};

export default AltaDeBiblioteca;
